import {
  filterSheetFooterTemplate,
  filterSheetTemplate,
  filterTitles,
  localNavigation,
  menuBarButtonForm,
  selectedNavigationTitle,
} from "./Selectors";
import { setActiveConfiguration } from "@otto-ec/heureka-components/filter";
import { FilterTitle } from "../filter/FilterTitle";
import { Filter } from "../filter/Filter";
import { FIRST_LEVEL_CHANGE_TRACKING_LABELS, SIDEBAR_CHANGE_TRACKING_LABELS } from "./FilterSheet";
import { FacetValueTag } from "../filter/FacetValueTag";
import { isSelected, SvelteFacetValue } from "../filter/SvelteFacetValue";
import { RefinementBar } from "../refinementBar/RefinementBar";
import type { TrackingLabels } from "../tracking/TrackingLabels";
import { FilterAccordion } from "../filter/FilterAccordion";
import { forEachElement, moveAllChildren } from "../util/Utils";
import { FilterSection } from "../filter/FilterSection";
import { FilterTitles } from "../filter/FilterTitles";
import { sheetV1 } from "@otto-ec/otto-components/sheet";
import { HeurekaElementFactory } from "../util/HeurekaElementFactory";

const FILTER_SHEET_ID = "heureka-filtersheet";
const FILTER_SHEET_CONTENT_CLASS = "heureka_filterSheet__content";
const FILTER_SHEET_ACTIONS_CLASS = "heureka_filterSheet__actions";
const FILTER_SHEET_CONTENT_SELECTOR = `.${FILTER_SHEET_CONTENT_CLASS}`;
const FILTER_SHEET_ACTIONS_SELECTOR = `.${FILTER_SHEET_ACTIONS_CLASS}`;
const FILTER_SHEET_LOADING_CLASS = `${FILTER_SHEET_CONTENT_CLASS}--loading`;

export default class OcFilterSheet {
  private static recentDetailedFilterId: string | undefined;
  static readonly factory = HeurekaElementFactory.byId(FILTER_SHEET_ID, OcFilterSheet);

  /*               */

  constructor(
    readonly sheet?: sheetV1,
    private readonly sheetContent: HTMLElement | null | undefined = sheet?.querySelector(FILTER_SHEET_CONTENT_SELECTOR),
    private readonly sheetActions: HTMLElement | null | undefined = sheet?.querySelector(FILTER_SHEET_ACTIONS_SELECTOR),
  ) {}

  public static pick() {
    return OcFilterSheet.factory.pick();
  }

  /*       */

  setSheet() {
    this.sheetContent?.appendChild(filterTitles());
    return this;
  }

  initializeSheet(updateChangeTracking = true) {
    const content = this.sheetContent;
    if (content) {
      setActiveConfiguration("sheetFirstLevel");
      OcFilterSheet.moveDesktopInlinedFiltersToSheet(content, updateChangeTracking);
      moveAllChildren(filterSheetFooterTemplate(), this.sheetActions, true);

      /*                                                                 */
      menuBarButtonForm()?.addEventListener("click", () => {
        FacetValueTag.factory.forEach((tag) => {
          tag.updateTrackingLabelsOnReferenced("deleted", "x_all_local");
        });
        SvelteFacetValue.factory
          .all(RefinementBar.facetValueTagsElement())
          .filter(isSelected)
          .forEach((tag) => {
            tag.updateTrackingLabelsOnReferenced("deleted", "x_all_local");
          });
      });
    }
    return this;
  }

  /*                       */

  deactivate() {
    const source = this.sheetContent;
    if (source) {
      OcFilterSheet.moveInlinedFiltersFrom(source, true, SIDEBAR_CHANGE_TRACKING_LABELS);
    }

    moveAllChildren(source, filterSheetTemplate(), true);
    moveAllChildren(this.sheetActions, filterSheetFooterTemplate(), true);
  }

  public lockContent() {
    if (this.sheet) {
      this.sheetContent?.classList.add(FILTER_SHEET_LOADING_CLASS);
    }
    return this;
  }

  public unlockContent() {
    this.sheetContent?.classList.remove(FILTER_SHEET_LOADING_CLASS);
    return this;
  }

  public close() {
    if (this.sheet) {
      this.deactivate();
      this.sheet.close();
    }
  }

  public open() {
    if (this.sheet) {
      this.sheet.open = true;
    }
  }

  public setScrollOffset(offset?: number) {
    if (typeof offset === "number") {
      const content: HTMLElement = this.sheet as HTMLElement;
      if (content) {
        content.scrollTop = offset;
      }
    }
    return this;
  }

  /*       */

  static moveDesktopInlinedFiltersToSheet(content: HTMLElement, updateChangeTracking: boolean) {
    FilterTitle.factory.forEach((filterTitle) => {
      if (updateChangeTracking) {
        Filter.updateChangeTrackingLabels(filterTitle.filterId, FIRST_LEVEL_CHANGE_TRACKING_LABELS);
      }
      filterTitle.attachFilter();
    });
  }

  public static scrollToRecentFilter(recentFilterId?: string) {
    OcFilterSheet.recentDetailedFilterId = recentFilterId || OcFilterSheet.recentDetailedFilterId;
    if (OcFilterSheet.recentDetailedFilterId) {
      const filterTitle = Filter.filterId(OcFilterSheet.recentDetailedFilterId)?.filterTitle?.elem;
      const content: HTMLElement = OcFilterSheet.pick().sheet as HTMLElement;
      if (filterTitle && content) {
        const footerHeight = FilterTitle.footer(content)?.elem.clientHeight || 0;
        const footerTop = content.scrollTop + content.clientHeight + content.clientTop - footerHeight;

        if (filterTitle.offsetTop + filterTitle.clientHeight >= footerTop && filterTitle.scrollIntoView) {
          filterTitle.scrollIntoView(true);
        }
      }
    }
  }

  static moveInlinedFiltersFrom(
    content: HTMLElement,
    updateChangeTracking: boolean,
    trackingLabel: Partial<TrackingLabels>,
  ) {
    FilterTitle.factory.forEach((filterTitle) => {
      if (updateChangeTracking) {
        Filter.updateChangeTrackingLabels(filterTitle.filterId, trackingLabel);
      }
      filterTitle.detachFilter();
    });
  }

  public static addErrorsOnFilterTitleValues(filterIds?: string[]) {
    const titles = filterTitles();
    if (titles) {
      const invalidIds = filterIds || FilterAccordion.invalidFilterIds();

      invalidIds.forEach((filterId) =>
        forEachElement(
          `.find_filterTitles__item[data-filter-id="${filterId}"]`,
          (item) => item.classList.add("find_filterTitles__item--invalid"),
          titles,
        ),
      );
    }
  }

  public static addLocalNavigationTitleValue() {
    const title = selectedNavigationTitle(localNavigation());
    const titles = filterTitles();
    if (titles && title) {
      const { overlayEntryTitle = "" } = title.dataset;
      forEachElement(".js_selectedNavigationFilterSheet", (elem) => (elem.innerText = overlayEntryTitle), titles);
    }
  }

  public static addInitialLoadError() {
    const errorMessage = FilterSection.template()?.errorMessage;
    if (errorMessage) {
      FilterTitles.template()?.elem.append(errorMessage.cloneNode(true));
    }
  }

  public addListeners() {
    this.sheet?.addEventListener("oc-sheet-before-open", () => {
      this.setSheet().initializeSheet();
    });
    this.sheet?.addEventListener("oc-sheet-before-close", () => {
      this.deactivate();
    });
  }
}
